import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, EmptyState } from "@/components/common";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";
import { REJECTED_STATUSES } from "@/lib/leadConfig";
import { toast } from "sonner";

export default function RejectedLeads() {
  const { data: all = [], isLoading } = useList(["leads", "rejected"], "/leads?status=all");
  const leads = all.filter((l) => REJECTED_STATUSES.includes(l.status));
  const invalidate = useInvalidate();
  const [busy, setBusy] = useState(null);

  const reopen = async (l) => {
    setBusy(l.id);
    try {
      await api.post(`/leads/${l.id}/reopen`, {});
      toast.success("Lead reopened and sent back to pipeline");
      invalidate(["leads", "dashboard"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader title="Rejected Leads" subtitle="Leads marked as rejected — review the reason or reopen them" />
      <Panel className="p-0">
        {isLoading ? (
          <p className="p-6 text-slate-400">Loading…</p>
        ) : leads.length === 0 ? (
          <EmptyState icon={Prohibit} title="No rejected leads" hint="Rejected leads will appear here with their reason." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Lead ID</TableHead>
                  <TableHead>Carrier Name</TableHead>
                  <TableHead className="hidden sm:table-cell">MC</TableHead>
                  <TableHead className="hidden sm:table-cell">USDOT</TableHead>
                  <TableHead>Rejected Reason</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} className="border-slate-100" data-testid={`rejected-lead-${l.id}`}>
                    <TableCell className="font-mono font-semibold text-blue-600">#{l.lead_number || "—"}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-800">{l.company}</p>
                      <StatusBadge status={l.status} className="mt-1" />
                    </TableCell>
                    <TableCell className="hidden sm:table-cell font-mono text-sm text-slate-600">{l.mc_number || "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell font-mono text-sm text-slate-600">{l.usdot || "—"}</TableCell>
                    <TableCell className="text-sm text-red-600 italic max-w-xs">{l.rejection_reason || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={busy === l.id} onClick={() => reopen(l)} className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold" data-testid={`reopen-${l.id}`}>
                        <ArrowCounterClockwise size={15} className="mr-1" />Reopen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}
